import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import Check from '@mui/icons-material/Check';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import Constants from 'style/constants';
import { DeviceType } from 'shared/models/device-type';

import ConfirmationStep from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/components/confirmation-step/confirmation-step';
import EnterDeviceIdStep from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/components/enter-device-id-step/enter-device-id-step';
import SelectTypeStep from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/components/select-type-step/select-type-step';
import ValidateTransmitterPopup from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/components/validate-transmitter-popup/validate-transmitter-popup';
import LinkableTransmitterStepType from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/domain/linkable-transmitter-step-type';
import LinkableTransmitterStepsProviderContext from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/providers/contexts/linkable-transmitter-steps-provider-context';
import LinkableTransmitterStepsProvider from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/linkable-transmitter-steps/providers/linkable-transmitter-steps-provider';
import styles from './linkable-transmitter-steps.module.scss';

type Props = {
  toggleLinkableTransmitterSteps: () => void;
  onTransmitterAdded: (createdDeviceId: string) => void;
  transmitterResidentSettings: DeviceType[];
};

const LinkableTransmitterStepsContent = (): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings', { keyPrefix: 'linkableDevicesModal.linkableTransmitterSteps' });
  const {
    steps,
    currentStep,
    activeStepIndex,
    isValidating,
    cancel,
    goToNextStep,
    goToPreviousStep,
    handleStepClick,
    isStepClickable,
    getIsNextStepDisabled,
    addTransmitter,
    form: { watch },
  } = use(LinkableTransmitterStepsProviderContext);
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === steps.length - 1;

  const selectedType = watch('type');

  const stepperStyle = {
    '& .MuiStepLabel-label': {
      color: '#585A66',
      fontWeight: '400',
    },
    '& .MuiStepLabel-label.Mui-active': {
      color: Constants.Colors.onSurface,
      fontWeight: '500',
    },
    '& .MuiStepLabel-label.Mui-completed': {
      color: Constants.Colors.onSurface,
      fontWeight: '400',
    },
  };

  const renderStepper = (): React.JSX.Element => (
    <Stepper nonLinear activeStep={activeStepIndex} sx={stepperStyle}>
      {steps.map((step, index) => (
        <Step key={step.translationKey} completed={index < activeStepIndex}>
          <StepButton onClick={() => handleStepClick(index)} disabled={!isStepClickable(index)}>
            {t(step.translationKey)}
          </StepButton>
        </Step>
      ))}
    </Stepper>
  );

  const renderStep = (): React.JSX.Element => {
    switch (currentStep.type) {
      case LinkableTransmitterStepType.SelectType:
        return <SelectTypeStep />;
      case LinkableTransmitterStepType.EnterDeviceId:
        return <EnterDeviceIdStep />;
      case LinkableTransmitterStepType.Confirm:
        return <ConfirmationStep />;
      default:
        return <></>;
    }
  };

  const renderNextButton = (): React.JSX.Element =>
    selectedType === DeviceType.Unknown ? (
      <></>
    ) : (
      <Button
        endIcon={<ArrowForwardRounded />}
        variant="contained"
        onClick={goToNextStep}
        disabled={getIsNextStepDisabled()}
        loading={isValidating}
        loadingIndicator={<CircularProgress size={20} />}
      >
        {t('nextButtonText')}
      </Button>
    );

  const renderActionButtons = (): React.JSX.Element => (
    <div className={styles.actionsContainer}>
      <Button className={styles.cancelButton} variant="text" onClick={cancel}>
        {t('cancelButtonText')}
      </Button>
      <div className={styles.navigationButtons}>
        {!isFirstStep && (
          <Button startIcon={<ArrowBackRounded />} variant="outlined" onClick={goToPreviousStep} disabled={isValidating}>
            {t('previousButtonText')}
          </Button>
        )}
        {isLastStep ? (
          <Button startIcon={<Check />} variant="contained" onClick={addTransmitter}>
            {t('addButtonText')}
          </Button>
        ) : (
          renderNextButton()
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className={styles.linkableTransmitterStepsContainer}>
        {renderStepper()}
        {renderStep()}
        {renderActionButtons()}
      </div>
      <ValidateTransmitterPopup />
    </>
  );
};

const LinkableTransmitterSteps = ({
  toggleLinkableTransmitterSteps,
  onTransmitterAdded,
  transmitterResidentSettings,
}: Readonly<Props>): React.JSX.Element => {
  return (
    <LinkableTransmitterStepsProvider
      toggleLinkableTransmitterSteps={toggleLinkableTransmitterSteps}
      onTransmitterAdded={onTransmitterAdded}
      transmitterResidentSettings={transmitterResidentSettings}
    >
      <LinkableTransmitterStepsContent />
    </LinkableTransmitterStepsProvider>
  );
};

export default LinkableTransmitterSteps;
